import React, {
  useCallback, useEffect, useMemo, useRef 
} from 'react';

import { Checkbox, FormControl } from '@primer/react';
import { VariableSizeList } from 'react-window';

import AutoSizer from 'react-virtualized-auto-sizer';

import findIndex from 'lodash/findIndex';
import sortBy from 'lodash/sortBy';

import ErrorLabel from '../../../common/Labels/ErrorLabel';
import VerticalGradientOverlay from '../../../common/overlays/VerticalGradientOverlay';

const sortLocationsForFilterList = (locations = []) => {
  if (!locations?.length) {
    return [];
  }
  return sortBy(locations, ({ name }) => name);
}

const EdgeLocationFilterItem = ({
  edgeLocation,
  onChange,
  disabled = false,
  selected = false,
}) => {
  const edgeName = edgeLocation?.name;
  const edgeId = edgeLocation?.id;
  return (
    <div
      id={`edge-selection-item-${edgeId}`}
      className="p-1 m-1 flex justify-between items-center w-full"
    >
      <FormControl disabled={disabled}>
        <Checkbox
          id={`edge-selection-checkbox-${edgeId}`}
          checked={selected}
          onChange={() => onChange(edgeId)}
        />
        <FormControl.Label>{edgeName}</FormControl.Label>
      </FormControl>
    </div>
  )
}

const ListRow = ({
  index,
  style,
  data
}) => {
  const {
    edgeLocations,
    isEdgeLocationSelected,
    listRef,
    rowHeights,
    onSelectionChanged,
  } = data;
  const rowRef = useRef({});
  const edgeLocation = edgeLocations?.length ? edgeLocations[index] : null;
  const edgeID = edgeLocation?.id;

  const setRowHeight = useCallback((rowIndex, size) => {
    listRef?.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [rowIndex]: size }
  }, [listRef, rowHeights]);

  useEffect(() => {
    if (rowRef?.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
  }, [index, setRowHeight]);

  return (
    <div style={style}>
      <EdgeLocationFilterItem
        key={edgeID}
        edgeLocation={edgeLocation}
        selected={isEdgeLocationSelected(edgeID)}
        onChange={onSelectionChanged}
      />
    </div>
  )
}

const VirtualizedListSection = ({
  width,
  edgeLocations,
  listRef,
  rowHeights,
  isEdgeLocationSelected,
  onSelectionChanged,
  height,
  itemSize,
  getRowHeight,
}) => {
  if (!edgeLocations?.length) {
    return (
      <div className="flex items-center justify-center w-full my-4" style={{ width: `${width}px` }}>
        <ErrorLabel size="xl">
          No edge locations were found for this pipeline
        </ErrorLabel>
      </div>
    )
  }

  return (
    <VariableSizeList
      ref={listRef}
      height={height}
      itemCount={edgeLocations?.length || 0}
      estimatedItemSize={itemSize}
      itemSize={getRowHeight}
      width={width}
      itemData={{
        edgeLocations,
        isEdgeLocationSelected,
        listRef,
        rowHeights,
        onSelectionChanged
      }}
    >
      {ListRow}
    </VariableSizeList>
  )
}

export const EdgeLocationFilterList = ({
  edgeLocations = [],
  selectedLocations = [],
  onSelectionChanged = () => {},
  height = 400,
  itemSize = 40,
  disableScrollFading = false,
}) => {
  const listRef = useRef({});
  const rowHeights = useRef({});

  const getRowHeight = useCallback((index) => {
    const rowHeight = rowHeights?.current[index]
    if (rowHeight && rowHeight < itemSize) {
      return itemSize;
    }
    return rowHeight || itemSize;
  }, [itemSize, rowHeights]);

  const isEdgeLocationSelected = useCallback((edgeId) => {
    const indexOfEdge = findIndex(selectedLocations, location => location.id === edgeId);
    return indexOfEdge > -1;
  }, [selectedLocations]);

  const sortedLocations = useMemo(() => sortLocationsForFilterList(edgeLocations), [edgeLocations])

  return (
    <div className="overflow-scroll py-4 flex-grow">
      {
        !disableScrollFading && (
          <VerticalGradientOverlay className="top-0 left-0 right-0" fromDirection="b" />
        )
      }
      <AutoSizer disableHeight>
        {({ width }) => (
          <VirtualizedListSection
            width={width}
            edgeLocations={sortedLocations}
            listRef={listRef}
            rowHeights={rowHeights}
            isEdgeLocationSelected={isEdgeLocationSelected}
            onSelectionChanged={onSelectionChanged}
            height={height}
            itemSize={itemSize}
            getRowHeight={getRowHeight}
          />
        )}
      </AutoSizer>
      {
        !disableScrollFading && (
          <VerticalGradientOverlay className="bottom-0 left-0 right-0" fromDirection="t" />
        )
      }
    </div>
  )
}

export default EdgeLocationFilterList;
